import { ArrowLeftIcon } from '@heroicons/react/24/solid'
import { Link } from 'react-router-dom'
import { withZod } from '@remix-validated-form/with-zod'
import { z } from 'zod'
import { zfd } from 'zod-form-data'
import { Button } from 'app/components/ui/button'
import { FormPassword } from 'app/components/ui/form-password'
import { SmartForm } from 'app/components/ui/smart-form'

const resetSchema = z
  .object({
    email: z.string().email(),
    password: z.string().min(8),
    confirmPassword: z.string().min(8),
  })
  .superRefine(({ confirmPassword, password }, ctx) => {
    if (confirmPassword !== password) {
      ctx.addIssue({
        code: `custom`,
        message: `The passwords did not match`,
        path: [`confirmPassword`],
      })
    }
  })

export const resetValidator = withZod(resetSchema)

export type ResetShadcnComponentProps = {
  email: string
  code: string
  success: boolean
  error: string | undefined
  isSubmitting: boolean
  onSubmit: (formData: FormData) => void
}

export function ResetShadcnComponent({
  email,
  code,
  success,
  error,
  isSubmitting,
  onSubmit,
}: ResetShadcnComponentProps) {
  if (error) {
    return (
      <div className="grid h-dvh place-items-center overflow-y-auto bg-surface-subtle p-4">
        <div className="w-full max-w-lg rounded-xl border border-default bg-surface p-6 shadow-sm sm:p-10">
          <h3 className="mb-5 text-center text-xl font-semibold">Something went wrong</h3>
          <p className="mb-5 text-center italic text-default">{error}</p>
          <p className="text-center text-default">
            You may have better success by requesting{' '}
            <Link to="/forgot" className="text-blue-600 hover:underline">
              a new password reset
            </Link>
            .
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="grid h-dvh place-items-center overflow-y-auto bg-surface-subtle p-4">
      <div className="w-full max-w-lg rounded-xl border border-default bg-surface p-6 shadow-sm sm:p-14">
        {success ? (
          <div className="max-w-[95vw]">
            <h3 className="mb-4 text-center text-xl font-semibold">Password Updated</h3>
            <p className="mb-6 text-center text-default">Your password has been updated successfully.</p>
            <Button variant="primary" size="lg" asChild className="w-full">
              <Link to="/login">Return to login</Link>
            </Button>
            <Link to="/login" className="mt-5 block text-center font-bold">
              <ArrowLeftIcon className="inline h-5 w-5" /> Back to login
            </Link>
          </div>
        ) : (
          <SmartForm
            validator={resetValidator}
            method="post"
            hideButtons={true}
            defaultValues={{ email }}
            hiddenInputs={[
              { name: 'email', value: email },
              { name: 'code', value: code },
            ]}
            className="flex! w-full flex-col!"
            preventSubmit
            onSubmit={(data) => {
              if (!data) return
              const fd = new FormData()
              fd.append('email', email)
              fd.append('code', code)
              fd.append('password', (data as any).password)
              fd.append('confirmPassword', (data as any).confirmPassword)
              onSubmit(fd)
            }}
          >
            <div className="col-span-6">
              <h2 className="text-center text-2xl font-semibold">Set new password</h2>
              <p className="mb-10 mt-2 text-center text-default">
                Enter your new password below to complete the reset.
              </p>
              <FormPassword
                label="Password"
                className="mb-4"
                labelClassName="text-ui-lg mb-2"
                cols={6}
                name="password"
              />
              <FormPassword
                label="Confirm Password"
                className="mb-6"
                labelClassName="text-ui-lg mb-2"
                cols={6}
                name="confirmPassword"
              />
              <Button
                variant="primary"
                size="lg"
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting}
                className="col-span-6 mb-5 w-full"
              >
                Set New Password
              </Button>
              <Link to="/login" className="block text-center font-bold">
                <ArrowLeftIcon className="inline h-5 w-5" /> Back to login
              </Link>
            </div>
          </SmartForm>
        )}
      </div>
    </div>
  )
}
